import mcpImg from 'common/assets/images/mixpanel-mcp.png';
import mobileSRImg from 'common/assets/images/tour-images/mobile-session-replay-tour.png';
import globalSidenavImg from 'common/assets/images/tour-images/global-sidenav-tour.png';
import {LOCALES, Translatable} from 'common/i18n';
import {promptFeedbackModal} from 'common/widgets/feedback-modal';
import {FeedbackForFeature} from 'common/widgets/feedback-modal/types';
import {FeatureGateTier} from 'common/report/constants';
import {Directions} from 'common/components/drop-menu/types';

export interface TourStepData {
  title: Translatable;
  description: Translatable;
  tourImage?: string;
  secondaryDescription?: Translatable;
  primaryButtonText?: Translatable;
  primaryButtonLink?: string;
  secondaryButtonText?: Translatable;
  secondaryButtonLink?: string;
  displayDismissButton?: boolean;
  onClickPrimaryButton?: () => void;
}

export interface TourData {
  stepData: TourStepData[];
  expirationDate?: Date;
  featureGateTier?: string[];
  maxWidth?: string;
  includeFirstStepInStepCount?: boolean;
  direction?: Directions;
}

export enum TourName {
  CONSISTENT_COLORS = `consistent-colors`,
  GLOBAL_SIDENAV = `global-sidenav`,
  MOBILE_SR_TOUR = `mobileSRTour`,
  LOCALIZATION = `localization`,
  ANNOTATIONS = `annotations`,
  ANNOTATIONS_TIMELINE = `annotations_timeline`,
  SPARK_COPILOT = `spark-copilot-2025-11-19`,
  COMMENTS_TOUR = `comments-tour`,
  MCP_GA = `mcp-ga`,
}

export const TOURS = {
  [TourName.CONSISTENT_COLORS]: {
    maxWidth: `300px`,
    stepData: [
      {
        secondaryButtonLink: `https://docs.mixpanel.com/docs/features/chart-customization#segment-coloring`,
        title: ({t}) => t(`tours.consistent-colors.title`),
        description: ({t}) => t(`tours.consistent-colors.description`),
        primaryButtonText: ({t}) => t(`tours.consistent-colors.cta`),
        secondaryButtonText: ({t}) => t(`tours.cta-docs`),
      },
    ],
  },
  [TourName.GLOBAL_SIDENAV]: {
    stepData: [
      {
        title: ({t}) => t(`tours.global-sidenav.title`),
        description: ({t}) => t(`tours.global-sidenav.description`),
        tourImage: globalSidenavImg,
        displayDismissButton: true,
        primaryButtonText: ({t}) => t(`tours.global-sidenav.learn-more`),
        primaryButtonLink: `https://www.loom.com/share/f2128bcf80ae455ebc7b568f49dc3df8?sid=1dcdce2f-1ac5-47f8-a62a-9865113be0cd`,
      },
    ],
    expirationDate: new Date(`2025-04-30T23:59:59Z`),
  },
  [TourName.MOBILE_SR_TOUR]: {
    stepData: [
      {
        title: ({t}) => t(`tours.mobile-session-replay.title`),
        description: ({t}) => t(`tours.mobile-session-replay.description`),
        tourImage: mobileSRImg,
        secondaryDescription: ({t}) => t(`tours.mobile-session-replay.secondary-description`),
        primaryButtonText: ({t}) => t(`tours.mobile-session-replay.cta`),
        primaryButtonLink: `https://docs.mixpanel.com/docs/session-replay/implement-session-replay`,
        secondaryButtonText: ({t}) => t(`tours.mobile-session-replay.secondary-cta`),
        secondaryButtonLink: `https://mixpanel.com/contact-us/sales/`,
        displayDismissButton: true,
      },
    ],
    expirationDate: new Date(`2025-08-19T23:59:59Z`),
    featureGateTier: [FeatureGateTier.Free, FeatureGateTier.Business],
  },
  [TourName.MCP_GA]: {
    stepData: [
      {
        title: ({t}) => t(`tours.mcp-ga.title`),
        description: ({t}) => t(`tours.mcp-ga.description`),
        tourImage: mcpImg,
        primaryButtonText: ({t}) => t(`tours.mcp-ga.cta`),
        primaryButtonLink: `https://docs.mixpanel.com/docs/features/mcp?utm_source=product&utm_medium=tour_modal&utm_campaign=mcp_ga#overview`,
        displayDismissButton: true,
      },
    ],
    expirationDate: new Date(`2026-04-10T23:59:59Z`),
  },
  [TourName.LOCALIZATION]: {
    stepData: [
      {
        title: ({t}) => t(`tours.localization.title`),
        description: ({t, language}) =>
          t(`tours.localization.description`, {
            currentLanguage: LOCALES.find((locale) => locale.code === language)?.displayName,
          }),
        primaryButtonText: ({t}) => t(`help.submit-feedback`),
        onClickPrimaryButton: () =>
          promptFeedbackModal({
            feature: FeedbackForFeature.Localization,
          }),
        displayDismissButton: true,
      },
    ],
  },
} as const satisfies Record<
  Exclude<
    TourName,
    TourName.ANNOTATIONS | TourName.ANNOTATIONS_TIMELINE | TourName.SPARK_COPILOT | TourName.COMMENTS_TOUR
  >,
  TourData
>;
